// src/components/FreeShippingBar.js
//
// Barra de progreso hacia envío gratis (despacho propio "La Negra").
// El envío está gratis si se alcanza el umbral de la zona O si ya viene gratis por otra
// vía (p.ej. una regla de producto: free_shipping_from_rule). En ambos casos la barra se
// pinta COMPLETA y verde, para no contradecir la línea "Costo de envío: Gratis".
//   - gratis             -> barra completa verde + "¡Tu envío es gratis!"
//   - subtotal <  umbral -> barra parcial naranja + "Te faltan $X para que tu envío sea gratis"
// Si el usuario baja la cantidad y deja de tener envío gratis, vuelve a naranja.
//
// No se muestra cuando:
//   - no hay umbral (threshold) válido,
//   - la dirección no es válida (isAddressValid === false),
//   - el método de envío es Alas (no aplica envío gratis de La Negra).

import React from 'react';
import '../css/components/FreeShippingBar.css';
import { formattedPrice } from '../utils/priceCalculations';

const FreeShippingBar = ({
  currentTotal = 0,
  threshold,
  isAddressValid = true,
  shippingMethod = null,
  // Estado real del envío según el método resuelto contra la API:
  //   true  -> ya es gratis (por monto de la zona o por regla de producto)
  //   false -> tiene costo (p.ej. zona de costo fijo): NUNCA decir "gratis"
  //   null  -> aún no se resolvió: caemos a comparar subtotal vs umbral
  isFreeShipping = null,
  centered = false,
}) => {
  const isAlas =
    typeof shippingMethod === 'string' &&
    shippingMethod.toLowerCase().includes('alas');

  const numericThreshold = Number(threshold);

  // Condiciones para no renderizar. Sin umbral válido, el envío gratis es imposible
  // en esta zona (sólo tiene métodos de costo fijo), así que la barra no aplica.
  if (!numericThreshold || numericThreshold <= 0 || !isAddressValid || isAlas) {
    return null;
  }

  const total = Number(currentTotal) || 0;
  const percentage = Math.min(100, (total / numericThreshold) * 100);
  // El costo real manda: si el envío tiene costo, no se anuncia "gratis" aunque el
  // subtotal supere el umbral. Sólo si aún no se resolvió comparamos contra el umbral.
  const reached = isFreeShipping === null ? total >= numericThreshold : isFreeShipping;
  const remaining = Math.max(0, numericThreshold - total);

  // El carrito ya supera el umbral y aun así el envío se cobra => en esta comuna alcanzar
  // el monto NO da envío gratis (costo fijo por regla). La barra no aplica: ocultarla en
  // vez de mostrar un "Te faltan $0" sin sentido.
  if (!reached && remaining <= 0) {
    return null;
  }

  return (
    <div className={`free-shipping-bar${reached ? ' is-complete' : ''}${centered ? ' free-shipping-bar--centered' : ''}`}>
      <div className="free-shipping-bar-track">
        <div
          className="free-shipping-bar-fill"
          style={{ width: `${reached ? 100 : percentage}%` }}
          role="progressbar"
          aria-valuenow={Math.round(reached ? 100 : percentage)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      <p className="free-shipping-bar-text">
        {reached ? (
          <>¡Tu envío es <strong>gratis</strong>!</>
        ) : (
          <>Te faltan <strong>${formattedPrice(remaining)}</strong> para que tu envío sea <strong>gratis</strong></>
        )}
      </p>
    </div>
  );
};

export default FreeShippingBar;
